import React, { useCallback, useEffect, useState } from "react";
import { Modal, ErrorAlert, LoadingState, EmptyState } from "./ui";
import { MODE_LABELS, confirmUndoImport } from "./ImportQuestionsModal";
import { fetchImportBatches, undoImportBatch } from "../utils/api";
import { showError, showSuccess } from "../utils/sweetAlert";
import "./ImportQuestions.css";

const formatWhen = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const plural = (n, word) => `${n} ${n === 1 ? word : `${word}s`}`;

const resultOf = (b) =>
  [
    b.sections_created > 0 && `${plural(b.sections_created, "section")} created`,
    b.sections_updated > 0 && `${plural(b.sections_updated, "section")} updated`,
    `+${b.created_count}`,
    b.updated_count > 0 && `${b.updated_count} updated`,
    b.deleted_count > 0 && `${b.deleted_count} deleted`,
    b.skipped_count > 0 && `${b.skipped_count} skipped`,
  ]
    .filter(Boolean)
    .join(", ");

export default function ImportHistoryModal({ sectionId, testSeriesId, scopeLabel, onClose, onChanged }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [undoingId, setUndoingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchImportBatches({ sectionId, testSeriesId });
      if (res?.success) setBatches(res.data || []);
      else if (res) setError(res.message || "Couldn't load the import history.");
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [sectionId, testSeriesId]);

  useEffect(() => {
    load();
  }, [load]);

  const undo = async (batch) => {
    const res = await confirmUndoImport(batch.remaining_questions, batch.remaining_sections);
    if (!res.isConfirmed) return;
    setUndoingId(batch.id);
    try {
      const { status, data } = await undoImportBatch(batch.id);
      if (status === 200 && data?.success) {
        showSuccess(data.message || "The import was undone.", "Import undone");
        onChanged?.();
      } else if (data) {
        showError(data.message || "Could not undo the import.");
      }
    } catch {
      showError("Could not reach the server. Check your connection and try again.");
    } finally {
      setUndoingId(null);
      load();
    }
  };

  return (
    <Modal
      size="lg"
      icon="bi bi-clock-history"
      title="Import history"
      subtitle={scopeLabel ? `CSV imports into ${scopeLabel}` : undefined}
      onClose={onClose}
    >
      <div className="iq">
        <ErrorAlert message={error} onRetry={load} />
        {loading ? (
          <LoadingState message="Loading imports…" />
        ) : batches.length === 0 ? (
          !error && (
            <EmptyState
              icon="bi bi-clock-history"
              title="No imports yet"
              message="CSV imports appear here, and the most recent ones can be undone."
            />
          )
        ) : (
          <div className="table-card">
            <div className="table-scroll">
              <table className="data-table iq-table iq-history">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>By</th>
                    <th>File</th>
                    <th>Mode</th>
                    <th>Result</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} className={b.undone_at ? "undone" : ""}>
                      <td className="iq-nowrap">{formatWhen(b.created_at)}</td>
                      <td>{b.admin?.name || "—"}</td>
                      <td>
                        <div className="cell-stack">
                          <strong>{b.original_filename || "—"}</strong>
                          {!b.section_id && <small>Whole test series</small>}
                        </div>
                      </td>
                      <td>{MODE_LABELS[b.mode] || b.mode}</td>
                      <td className="iq-nowrap">
                        {b.undone_at ? `Undone on ${formatWhen(b.undone_at)}` : resultOf(b)}
                      </td>
                      <td className="iq-nowrap">
                        {b.can_undo && !b.undone_at && (
                          <button
                            type="button"
                            className="btn-danger btn-sm"
                            onClick={() => undo(b)}
                            disabled={undoingId != null}
                          >
                            {undoingId === b.id ? (
                              <span className="btn-spinner"></span>
                            ) : (
                              <i className="bi bi-arrow-counterclockwise"></i>
                            )}
                            Undo
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
